import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { updateProfile } from 'firebase/auth';

import { removePassCodeur, grantPassCodeurAdmin } from '../lib/stripe-service';
import { usePassCodeur } from '../lib/pass-codeur-context';
import { useToast } from '../lib/use-toast';
import AliasModal from '../components/AliasModal';
import SitemapManager from '../components/admin/SitemapManager';
import SEO from '../components/SEO';
import { Pencil, XCircle, AlertTriangle, RefreshCw, Crown } from 'lucide-react';
import ThemeToggle from '@/components/ui/theme-toggle';

// Import des composants UI
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from '../components/ui/label';
import UserAvatar from '../components/UserAvatar';



const Profile = () => {
  const { currentUser, userData, logout, updateUserPreferences, error } = useAuth();
  const { hasAccessToCourses, loadPassCodeurStatus } = usePassCodeur();
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Si l'utilisateur n'est pas connecté, redirigez-le vers la page de connexion
  if (!currentUser) {
    navigate('/login');
    return null;
  }

  // Afficher un message d'erreur si les données utilisateur n'ont pas pu être chargées
  useEffect(() => {
    if (error) {
      toast({
        title: "Erreur",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Déconnexion réussie",
        description: "Vous êtes maintenant déconnecté.",
        variant: "success",
      });
      navigate('/login');
    } catch (error) {
      console.error("Erreur de déconnexion:", error.message);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la déconnexion.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await updateProfile(currentUser, { displayName });
      toast({
        title: "Profil mis à jour",
        description: "Vos informations ont été mises à jour avec succès.",
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur de mise à jour:", error.message);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour du profil.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };





  const handleRemovePassCodeur = async () => {
    try {
      setIsLoading(true);
      await removePassCodeur(currentUser.uid);
      
      // Rafraîchir le statut du pass dans le contexte
      await loadPassCodeurStatus();
      
      toast({
        title: "Pass Codeur supprimé",
        description: "Votre Pass Codeur a été supprimé avec succès.",
        variant: "destructive",
      });
    } catch (error) {
      console.error("Erreur lors de la suppression du Pass Codeur:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la suppression du Pass Codeur.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrantPassCodeur = async () => {
    try {
      setIsLoading(true);
      await grantPassCodeurAdmin(currentUser.uid);
      
      // Rafraîchir le statut du pass dans le contexte
      await loadPassCodeurStatus();
      
      toast({
        title: "Pass Codeur accordé",
        description: "Le Pass Codeur a été accordé avec succès.",
        variant: "success",
      });
    } catch (error) {
      console.error("Erreur lors de l'attribution du Pass Codeur:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'accorder le Pass Codeur.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour réinitialiser toute la progression
  const handleResetAllProgress = () => {
    setShowResetConfirmation(true);
  };

  // Fonction pour confirmer la réinitialisation globale
  const confirmResetAllProgress = () => {
    try {
      // Supprimer toutes les données de progression et éditeurs
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // Supprimer les clés de progression des cours et les éditeurs
        if (key && (key === 'courseProgress' || key.includes('_editor') || key.includes('baseprogrammation'))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      setShowResetConfirmation(false);
      toast({
        title: "Progression réinitialisée",
        description: "Toute votre progression a été effacée avec succès.",
        variant: "success",
      });
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la réinitialisation.",
        variant: "destructive",
      });
    }
  };

  // Déterminer la méthode d'authentification
  const getAuthMethod = () => {
    const providerId = currentUser.providerData[0]?.providerId;
    if (providerId === 'google.com') return 'Google';
    if (providerId === 'password') return 'Email et mot de passe';
    return 'Inconnu';
  };

  return (
    <>
      <SEO 
        title="Profil utilisateur - Mon Premier Jeu"
        description="Gérez votre profil, vos préférences et vos informations personnelles. Accès aux outils d'administration pour les utilisateurs autorisés."
        keywords="profil utilisateur, paramètres compte, administration, gestion progression"
        url="/profile"
        type="webapp"
      />
      {/* Boîte de dialogue de confirmation pour la réinitialisation */}
      <AlertDialog open={showResetConfirmation} onOpenChange={setShowResetConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toute votre progression dans tous les cours ainsi que votre code 
              dans les éditeurs seront définitivement effacés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmResetAllProgress}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Réinitialiser tout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    <div className="container mx-auto px-4 py-8">
          <Card className="w-full shadow-lg">
            <CardHeader>
              <div className="flex items-center space-x-4">
                <UserAvatar user={currentUser} size="h-20 w-20" />
                <div>
                  <CardTitle className="text-2xl font-bold">{currentUser.displayName || 'Utilisateur'}</CardTitle>
                  <CardDescription>
                    Gérez vos informations personnelles
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {isEditing ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Nom d'affichage</Label>
                    <Input
                      id="displayName"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsEditing(false)} 
                      disabled={isLoading}
                    >
                      Annuler
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="space-y-4">
                    {/* Section Alias - uniquement pour les utilisateurs avec Pass Codeur */}
                    {hasAccessToCourses() && (
                      <div>
                        <div className="flex items-center gap-2">
                          <Label>Alias</Label>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setShowAliasModal(true)}
                            className="h-7 w-7 p-0"
                            aria-label="Modifier l'alias"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              try {
                                await updateUserPreferences({ alias: null });
                                toast({
                                  title: "Alias supprimé",
                                  description: "Votre alias a été supprimé.",
                                  variant: "destructive",
                                });
                              } catch (error) {
                                toast({
                                  title: "Erreur",
                                  description: "Impossible de supprimer l'alias.",
                                  variant: "destructive",
                                });
                              }
                            }}
                            className="h-7 w-7 p-0 text-red-500 hover:bg-red-100"
                            aria-label="Supprimer l'alias"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                        <span className="text-base text-foreground font-medium block mt-1">
                          {userData?.alias || 'Non défini'}
                        </span>
                        {userData?.alias ? (
                          <div className="mt-2 space-y-2">
                            <p className="text-sm text-muted-foreground">
                              Vos liens personnels (cliquez pour copier) :
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <button 
                                onClick={() => {
                                  const url = `${window.location.origin}/project/${userData.alias}`;
                                  navigator.clipboard.writeText(url);
                                  toast({
                                    title: "Lien copié !",
                                    description: "Le lien a été copié dans le presse-papier.",
                                    variant: "success",
                                  });
                                }}
                                className="inline-flex items-center px-3 py-1 text-xs rounded-md bg-blue-100 hover:bg-blue-200 text-blue-800 transition-colors cursor-pointer"
                              >
                                /project/{userData.alias}
                              </button>
                              <button 
                                onClick={() => {
                                  const url = `${window.location.origin}/play/${userData.alias}`;
                                  navigator.clipboard.writeText(url);
                                  toast({
                                    title: "Lien copié !",
                                    description: "Le lien a été copié dans le presse-papier.",
                                    variant: "success",
                                  });
                                }}
                                className="inline-flex items-center px-3 py-1 text-xs rounded-md bg-blue-100 hover:bg-blue-200 text-blue-800 transition-colors cursor-pointer"
                              >
                                /play/{userData.alias}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground mt-2">
                            Votre alias sera utilisé pour vos URLs personnelles : /project/votre-alias et /play/votre-alias
                          </p>
                        )}
                      </div>
                    )}
                    <div>
                      <Label>Email</Label>
                      <p className="text-sm text-muted-foreground">{currentUser.email}</p>
                    </div>
                    <div>
                      <Label>Méthode de connexion</Label>
                      <p className="text-sm text-muted-foreground">{getAuthMethod()}</p>
                    </div>
                    <div>
                      <Label>Type de compte</Label>
                      <p className="text-sm text-muted-foreground">
                        {userData?.userType === 'admin' ? 'Administrateur' : 'Utilisateur'}
                      </p>
                    </div>
                    <div>
                      <Label>Thème d'affichage</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <ThemeToggle />
                        <span className="text-sm text-muted-foreground">Basculer entre le mode clair et sombre</span>
                      </div>
                    </div>
                  </div>
                  {/* Bouton de réinitialisation de la progression */}
                  <div className="mb-4">
                    <Button 
                      variant="outline"
                      onClick={handleResetAllProgress}
                      disabled={isLoading}
                      className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Réinitialiser toute ma progression
                    </Button>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setIsEditing(true)}
                      disabled={isLoading}
                    >
                      Modifier le profil
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleLogout}
                      disabled={isLoading}
                    >
                      Se déconnecter
                    </Button>
                  </div>
                </>
              )}

              {/* Section Admin */}
              {userData?.userType === 'admin' && (
                <div className="border-t pt-6 mt-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-lg font-semibold text-blue-600">Actions Administrateur</Label>
                      <p className="text-sm text-muted-foreground">
                        Actions réservées aux administrateurs
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium">Pass Codeur</h4>
                          <p className="text-sm text-muted-foreground">
                            Statut actuel : {hasAccessToCourses() ? 
                              <span className="text-green-600 font-medium">Actif</span> : 
                              <span className="text-red-600 font-medium">Inactif</span>
                            }
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {!hasAccessToCourses() && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={handleGrantPassCodeur}
                              disabled={isLoading}
                              className="gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                            >
                              <Crown className="h-4 w-4" />
                              Donner le Pass
                            </Button>
                          )}
                          {hasAccessToCourses() && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleRemovePassCodeur}
                              disabled={isLoading}
                              className="gap-2"
                            >
                              <AlertTriangle className="h-4 w-4" />
                              Supprimer le Pass
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Section SEO et Sitemap pour les admins */}
              {userData?.userType === 'admin' && (
                <div className="border-t pt-6 mt-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-lg font-semibold text-blue-600">Gestion SEO</Label>
                      <p className="text-sm text-muted-foreground">
                        Outils de référencement et génération de sitemap
                      </p>
                    </div>
                    
                    {/* Intégrer le SitemapManager */}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <SitemapManager />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

      {hasAccessToCourses() && (
        <AliasModal 
          isOpen={showAliasModal} 
          onClose={() => setShowAliasModal(false)} 
          redirectOnSuccess={false}
        />
      )}
    </div>
    </>
  );
};

export default Profile; 